import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  getAccountingCraAccess,
  listAccountingReviewCraAccess,
  updateAccountingCraAccessAuthorization,
  updateAccountingCraAccessChecklist,
  updateAccountingCraAccessNotes,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";
import {
  accountingContextFromSearchParams,
  accountingPath,
  hasAccountingContext,
} from "./accountingRouteContext";

const statusTone = {
  not_started: "border-slate-200 bg-slate-50 text-slate-700",
  ready: "border-emerald-200 bg-emerald-50 text-emerald-700",
  needs_setup: "border-amber-200 bg-amber-50 text-amber-700",
  locked_out: "border-rose-200 bg-rose-50 text-rose-700",
  waiting_for_cra_code: "border-sky-200 bg-sky-50 text-sky-700",
  request_sent: "border-sky-200 bg-sky-50 text-sky-700",
  waiting_for_client: "border-amber-200 bg-amber-50 text-amber-700",
  confirmed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  expired: "border-rose-200 bg-rose-50 text-rose-700",
  needs_resend: "border-amber-200 bg-amber-50 text-amber-700",
  not_needed: "border-slate-200 bg-white text-slate-600",
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function Badge({ value, label }) {
  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
        statusTone[value] || "border-slate-200 bg-slate-50 text-slate-700"
      }`}
    >
      {label || value || "Not started"}
    </span>
  );
}

function SelectField({ label, value, options, onChange, disabled = false }) {
  return (
    <label className="block text-sm font-semibold text-slate-800">
      {label}
      <select
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="mt-1 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 disabled:bg-slate-100 disabled:text-slate-500"
      >
        {(options || []).map((option) => (
          <option key={option.code} value={option.code}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextField({ label, value, onChange, placeholder = "", disabled = false }) {
  return (
    <label className="block text-sm font-semibold text-slate-800">
      {label}
      <input
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="mt-1 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 disabled:bg-slate-100"
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, placeholder = "", disabled = false }) {
  return (
    <label className="block text-sm font-semibold text-slate-800">
      {label}
      <textarea
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        rows={4}
        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-800 disabled:bg-slate-100"
      />
    </label>
  );
}

function clientContext(row) {
  return {
    business_id: row?.business_id,
    salon_id: row?.salon_id,
  };
}

function buildChecklistDraft(setup) {
  return {
    personal_account_status: setup?.personal_account_status || "not_started",
    business_account_status: setup?.business_account_status || "not_started",
    has_business_number: Boolean(setup?.has_business_number),
    business_number_last4: setup?.business_number_last4 || "",
    gst_hst_account_status: setup?.gst_hst_account_status || "not_started",
    payroll_account_status: setup?.payroll_account_status || "not_started",
    corporate_tax_account_status: setup?.corporate_tax_account_status || "not_started",
    client_notes: setup?.client_notes || "",
  };
}

function buildAuthorizationDraft(setup) {
  return {
    representative_identifier_type: setup?.representative_identifier_type || "repid",
    representative_identifier_label: setup?.representative_identifier_label || "",
    authorization_level: setup?.authorization_level || "level_2",
    personal_authorization_status:
      setup?.personal_authorization_status || "not_started",
    business_authorization_status:
      setup?.business_authorization_status || "not_started",
    internal_notes: setup?.internal_notes || "",
  };
}

export default function SalonBusinessCpaCraAccess() {
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const context = useMemo(
    () => accountingContextFromSearchParams(new URLSearchParams(location.search)),
    [location.search]
  );
  const roleKey = String(user?.role || "").toLowerCase();
  const canReview = Boolean(user?.is_super_admin || roleKey === "salonbusinesscpa_accountant");
  const hasContext = hasAccountingContext(context);

  const [setup, setSetup] = useState(null);
  const [clients, setClients] = useState([]);
  const [checklistDraft, setChecklistDraft] = useState(buildChecklistDraft(null));
  const [authorizationDraft, setAuthorizationDraft] = useState(buildAuthorizationDraft(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      if (canReview) {
        const listData = await listAccountingReviewCraAccess(token);
        const nextClients = Array.isArray(listData?.clients) ? listData.clients : [];
        setClients(nextClients);
        if (!hasContext && nextClients.length) {
          setSetup(null);
          setChecklistDraft(buildChecklistDraft(null));
          setAuthorizationDraft(buildAuthorizationDraft(null));
          return;
        }
      }
      const data = await getAccountingCraAccess(token, hasContext ? context : {});
      setSetup(data);
      setChecklistDraft(buildChecklistDraft(data));
      setAuthorizationDraft(buildAuthorizationDraft(data));
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load CRA access setup.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.search, canReview]);

  const statusOptions = setup?.account_status_options || clients[0]?.account_status_options || [];
  const programStatusOptions =
    setup?.program_account_status_options || clients[0]?.program_account_status_options || [];
  const authorizationStatusOptions =
    setup?.authorization_status_options || clients[0]?.authorization_status_options || [];
  const representativeOptions =
    setup?.representative_identifier_options ||
    clients[0]?.representative_identifier_options ||
    [
      { code: "repid", label: "CRA RepID" },
      { code: "groupid", label: "CRA GroupID" },
    ];
  const authorizationLevelOptions =
    setup?.authorization_level_options || clients[0]?.authorization_level_options || [];

  const saveChecklist = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving("checklist");
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingCraAccessChecklist(token, {
        ...(hasContext ? context : {}),
        ...checklistDraft,
      });
      setSetup(updated);
      setChecklistDraft(buildChecklistDraft(updated));
      setAuthorizationDraft(buildAuthorizationDraft(updated));
      setNotice("CRA account check saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save CRA account check.");
    } finally {
      setSaving("");
    }
  };

  const saveAuthorization = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving("authorization");
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingCraAccessAuthorization(token, {
        ...(hasContext ? context : {}),
        ...authorizationDraft,
      });
      setSetup(updated);
      setChecklistDraft(buildChecklistDraft(updated));
      setAuthorizationDraft(buildAuthorizationDraft(updated));
      setNotice("CRA authorization status saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save CRA authorization status.");
    } finally {
      setSaving("");
    }
  };

  const saveClientConfirmation = async () => {
    if (!token) return;
    setSaving("confirm");
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingCraAccessAuthorization(token, {
        ...(hasContext ? context : {}),
        client_confirmed_at_utc: new Date().toISOString(),
        personal_authorization_status:
          authorizationDraft.personal_authorization_status === "waiting_for_client"
            ? "confirmed"
            : authorizationDraft.personal_authorization_status,
        business_authorization_status:
          authorizationDraft.business_authorization_status === "waiting_for_client"
            ? "confirmed"
            : authorizationDraft.business_authorization_status,
      });
      setSetup(updated);
      setChecklistDraft(buildChecklistDraft(updated));
      setAuthorizationDraft(buildAuthorizationDraft(updated));
      setNotice("Confirmation saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save confirmation.");
    } finally {
      setSaving("");
    }
  };

  const saveNotes = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving("notes");
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingCraAccessNotes(token, {
        ...(hasContext ? context : {}),
        client_notes: checklistDraft.client_notes,
        internal_notes: authorizationDraft.internal_notes,
      });
      setSetup(updated);
      setChecklistDraft(buildChecklistDraft(updated));
      setAuthorizationDraft(buildAuthorizationDraft(updated));
      setNotice("Notes saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save notes.");
    } finally {
      setSaving("");
    }
  };

  const selectedClientLabel = setup
    ? `${setup.salon_name || "Salon"} / Salon ID ${setup.salon_id}`
    : "";

  return (
    <SalonBusinessCpaShell
      title="CRA Access Setup"
      description="Check CRA account readiness and track the steps needed to authorize Ellyanna Accounting as the representative."
      actions={
        <button
          type="button"
          onClick={loadData}
          className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      }
    >
      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
          {notice}
        </div>
      ) : null}

      {canReview ? (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Client CRA setup
              </p>
              <h2 className="mt-2 text-lg font-semibold text-slate-950">
                {selectedClientLabel || "Choose a client"}
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Accountants see assigned Salon IDs. Super admins see all Ellyanna Accounting clients.
              </p>
            </div>
            {hasContext ? (
              <button
                type="button"
                onClick={() => navigate("/cra-access")}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Client list
              </button>
            ) : null}
          </div>
          {!hasContext ? (
            <div className="mt-5 grid gap-3 lg:grid-cols-2">
              {loading ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  Loading clients...
                </div>
              ) : clients.length ? (
                clients.map((client) => (
                  <Link
                    key={`${client.business_id}:${client.salon_id}`}
                    to={accountingPath("/cra-access", clientContext(client))}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4 transition hover:bg-white"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-slate-950">
                        {client.salon_name || "Salon"}
                      </h3>
                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                        Salon ID {client.salon_id}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      {client.business_name || "Business"}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Badge
                        value={client.personal_authorization_status}
                        label={`Personal: ${client.personal_authorization_status_label}`}
                      />
                      <Badge
                        value={client.business_authorization_status}
                        label={`Business: ${client.business_authorization_status_label}`}
                      />
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      {client.next_step}
                    </p>
                  </Link>
                ))
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  No Ellyanna Accounting clients are available yet.
                </div>
              )}
            </div>
          ) : null}
        </section>
      ) : null}

      {loading && !setup ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading CRA setup...
        </div>
      ) : null}

      {setup ? (
        <>
          <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.5fr)]">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Current next step
                </p>
                <h2 className="mt-2 text-xl font-semibold text-slate-950">
                  {setup.next_step}
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  We will never ask for CRA passwords, MFA codes, or full SIN. This page only tracks whether access is ready and whether the representative request has been confirmed.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Representative
                </p>
                <p className="mt-2 text-lg font-semibold text-slate-950">
                  {setup.representative_identifier_type_label || "CRA RepID"}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  {setup.representative_identifier_label || "Identifier will be provided by Ellyanna Accounting."}
                </p>
                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Default setup currently uses CRA RepID. GroupID is available for future team setup.
                </p>
              </div>
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-4">
            {[
              ["CRA My Account", setup.personal_account_status, setup.personal_account_status_label],
              ["My Business Account", setup.business_account_status, setup.business_account_status_label],
              ["Personal auth", setup.personal_authorization_status, setup.personal_authorization_status_label],
              ["Business auth", setup.business_authorization_status, setup.business_authorization_status_label],
            ].map(([label, value, statusLabel]) => (
              <div key={label} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {label}
                </p>
                <div className="mt-3">
                  <Badge value={value} label={statusLabel} />
                </div>
              </div>
            ))}
          </section>

          <form onSubmit={saveChecklist} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">CRA account check</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Confirm what the client can already access before sending or confirming representative requests.
                </p>
              </div>
              <button
                type="submit"
                disabled={saving === "checklist"}
                className="rounded-lg bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
              >
                {saving === "checklist" ? "Saving..." : "Save account check"}
              </button>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <SelectField
                label="CRA My Account"
                value={checklistDraft.personal_account_status}
                options={statusOptions}
                onChange={(value) =>
                  setChecklistDraft((current) => ({ ...current, personal_account_status: value }))
                }
              />
              <SelectField
                label="My Business Account"
                value={checklistDraft.business_account_status}
                options={statusOptions}
                onChange={(value) =>
                  setChecklistDraft((current) => ({ ...current, business_account_status: value }))
                }
              />
              <label className="flex min-h-11 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-800 md:mt-6">
                <input
                  type="checkbox"
                  checked={checklistDraft.has_business_number}
                  onChange={(event) =>
                    setChecklistDraft((current) => ({
                      ...current,
                      has_business_number: event.target.checked,
                    }))
                  }
                />
                Business number is ready
              </label>
              <TextField
                label="Business number last 4"
                value={checklistDraft.business_number_last4}
                onChange={(value) =>
                  setChecklistDraft((current) => ({ ...current, business_number_last4: value }))
                }
                placeholder="1234"
              />
              <SelectField
                label="GST/HST account"
                value={checklistDraft.gst_hst_account_status}
                options={programStatusOptions}
                onChange={(value) =>
                  setChecklistDraft((current) => ({ ...current, gst_hst_account_status: value }))
                }
              />
              <SelectField
                label="Payroll account"
                value={checklistDraft.payroll_account_status}
                options={programStatusOptions}
                onChange={(value) =>
                  setChecklistDraft((current) => ({ ...current, payroll_account_status: value }))
                }
              />
              <SelectField
                label="Corporate tax account"
                value={checklistDraft.corporate_tax_account_status}
                options={programStatusOptions}
                onChange={(value) =>
                  setChecklistDraft((current) => ({
                    ...current,
                    corporate_tax_account_status: value,
                  }))
                }
              />
            </div>
          </form>

          <form onSubmit={saveAuthorization} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Authorize Ellyanna Accounting</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Track the CRA representative request. Clients stay in control and can remove representative access in CRA later.
                </p>
              </div>
              <button
                type="submit"
                disabled={saving === "authorization"}
                className="rounded-lg bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
              >
                {saving === "authorization" ? "Saving..." : "Save authorization"}
              </button>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <SelectField
                label="Personal authorization"
                value={authorizationDraft.personal_authorization_status}
                options={authorizationStatusOptions}
                onChange={(value) =>
                  setAuthorizationDraft((current) => ({
                    ...current,
                    personal_authorization_status: value,
                  }))
                }
              />
              <SelectField
                label="Business authorization"
                value={authorizationDraft.business_authorization_status}
                options={authorizationStatusOptions}
                onChange={(value) =>
                  setAuthorizationDraft((current) => ({
                    ...current,
                    business_authorization_status: value,
                  }))
                }
              />
              <SelectField
                label="Representative ID type"
                value={authorizationDraft.representative_identifier_type}
                options={representativeOptions}
                disabled={!setup.can_edit_internal}
                onChange={(value) =>
                  setAuthorizationDraft((current) => ({
                    ...current,
                    representative_identifier_type: value,
                  }))
                }
              />
              <TextField
                label="Representative label"
                value={authorizationDraft.representative_identifier_label}
                disabled={!setup.can_edit_internal}
                onChange={(value) =>
                  setAuthorizationDraft((current) => ({
                    ...current,
                    representative_identifier_label: value,
                  }))
                }
                placeholder="CRA RepID label"
              />
              <SelectField
                label="Access level"
                value={authorizationDraft.authorization_level}
                options={authorizationLevelOptions}
                disabled={!setup.can_edit_internal}
                onChange={(value) =>
                  setAuthorizationDraft((current) => ({ ...current, authorization_level: value }))
                }
              />
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  CRA request timing
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-700">
                  Sent: {formatDateTime(setup.request_sent_at_utc)}
                  <br />
                  Expires: {formatDateTime(setup.request_expires_at_utc)}
                </p>
              </div>
            </div>
            <div className="mt-5 rounded-lg border border-sky-200 bg-sky-50 p-4">
              <p className="text-sm font-semibold text-slate-950">
                Client confirmation
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                When the client confirms the request in CRA, save the confirmation here. Some CRA representative requests expire if they are not confirmed in time.
              </p>
              <button
                type="button"
                onClick={saveClientConfirmation}
                disabled={saving === "confirm"}
                className="mt-3 rounded-lg border border-sky-300 bg-white px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60"
              >
                {saving === "confirm" ? "Saving..." : "Mark client confirmed"}
              </button>
            </div>
          </form>

          <form onSubmit={saveNotes} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Notes and history</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Use simple notes for next steps. Internal notes are only visible to accountants and super admins.
                </p>
              </div>
              <button
                type="submit"
                disabled={saving === "notes"}
                className="rounded-lg bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
              >
                {saving === "notes" ? "Saving..." : "Save notes"}
              </button>
            </div>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <TextAreaField
                label="Client-facing notes"
                value={checklistDraft.client_notes}
                onChange={(value) =>
                  setChecklistDraft((current) => ({ ...current, client_notes: value }))
                }
                placeholder="What the client should do next"
              />
              <TextAreaField
                label="Internal notes"
                value={authorizationDraft.internal_notes}
                disabled={!setup.can_edit_internal}
                onChange={(value) =>
                  setAuthorizationDraft((current) => ({ ...current, internal_notes: value }))
                }
                placeholder="Private accounting team notes"
              />
            </div>
            <div className="mt-5 grid gap-3">
              {(setup.recent_audit || []).length ? (
                setup.recent_audit.map((event) => (
                  <div key={event.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                      <p className="text-sm font-semibold text-slate-950">
                        {String(event.action || "").replaceAll("_", " ")}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(event.occurred_at_utc)}
                      </p>
                    </div>
                    {event.field_name ? (
                      <p className="mt-1 text-xs leading-5 text-slate-600">
                        {event.field_name}: {event.old_value || "-"} to {event.new_value || "-"}
                      </p>
                    ) : null}
                    <p className="mt-1 text-xs text-slate-500">
                      {event.actor_name || event.actor_email || "System"}
                    </p>
                  </div>
                ))
              ) : (
                <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                  No CRA setup history yet.
                </p>
              )}
            </div>
          </form>
        </>
      ) : canReview && !hasContext ? null : (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600">
          Choose a client or refresh the page to open CRA access setup.
        </div>
      )}
    </SalonBusinessCpaShell>
  );
}
